import type { QueryParams } from './client';

/**
 * Ключ = ['tc', <эндпоинт>, <нормализованные параметры>].
 * Нормализация обязательна: без неё { page, dest } и { dest, page } дают
 * два разных ключа на один и тот же запрос.
 */
function norm(params: QueryParams | undefined): Record<string, string> {
  const entries = Object.entries(params ?? {})
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]): [string, string] => [
      key,
      Array.isArray(value) ? JSON.stringify(value) : String(value),
    ])
    .sort(([a], [b]) => a.localeCompare(b));
  return Object.fromEntries(entries);
}

export const keys = {
  root: ['tc'] as const,
  auth: () => ['tc', 'auth'] as const,
  accountProfile: () => ['tc', 'account-profile'] as const,
  accountSecurity: () => ['tc', 'account-security'] as const,
  accountDeletion: () => ['tc', 'account-deletion'] as const,
  workspace: () => ['tc', 'workspace'] as const,
  searchProfiles: (includeArchived = false) => ['tc', 'search-profiles', includeArchived] as const,
  searchProfileHistory: (profileId: string) => ['tc', 'search-profile-history', profileId] as const,
  signalRules: (includeArchived = false) => ['tc', 'signal-rules', includeArchived] as const,
  signalRuleJournal: (ruleId: string) => ['tc', 'signal-rule-journal', ruleId] as const,
  notifications: (params: QueryParams) => ['tc', 'notifications', norm(params)] as const,
  notificationsSummary: () => ['tc', 'notifications-summary'] as const,
  notificationPreferences: () => ['tc', 'notification-preferences'] as const,
  dashboard: (params?: QueryParams) => ['tc', 'dashboard', norm(params)] as const,
  directions: (params: QueryParams) => ['tc', 'directions', norm(params)] as const,
  stays: (params: QueryParams) => ['tc', 'stays', norm(params)] as const,
  staysFacets: (params: QueryParams) => ['tc', 'stays-facets', norm(params)] as const,
  stay: (stayKey: string) => ['tc', 'stay', stayKey] as const,
  stayBundle: (stayKey: string, historyDays: number, watchId: number | null) =>
    ['tc', 'stay', stayKey, 'bundle', historyDays, watchId] as const,
  variants: (stayKey: string) => ['tc', 'stay', stayKey, 'variants'] as const,
  stayHistory: (stayKey: string, days: number) =>
    ['tc', 'stay', stayKey, 'history', days] as const,
  combination: (stayKey: string) => ['tc', 'stay', stayKey, 'combination'] as const,
  operators: (params: QueryParams) => ['tc', 'operators', norm(params)] as const,
  stayOperators: (stayKey: string) => ['tc', 'stay', stayKey, 'operators'] as const,
  resortAlternatives: (stayKey: string) => ['tc', 'stay', stayKey, 'resorts'] as const,
  flights: (params: QueryParams) => ['tc', 'flights', norm(params)] as const,
  matrix: (params: QueryParams) => ['tc', 'matrix', norm(params)] as const,
  departureQuotes: (params: QueryParams) => ['tc', 'departure-quotes', norm(params)] as const,
  events: (params: QueryParams) => ['tc', 'events', norm(params)] as const,
  eventsSummary: (params: QueryParams) => ['tc', 'events-summary', norm(params)] as const,
  sources: (params?: QueryParams) => ['tc', 'sources', norm(params)] as const,
  meta: () => ['tc', 'meta'] as const,
  resorts: (country: string) => ['tc', 'meta', 'resorts', country] as const,
  watch: () => ['tc', 'watch'] as const,
  pages: () => ['tc', 'pages'] as const,
  pageHistory: (id: number) => ['tc', 'pages', id, 'history'] as const,
  partySettings: () => ['tc', 'settings', 'party'] as const,
  status: () => ['tc', 'status'] as const,
  usage: (params?: QueryParams) => ['tc', 'usage', norm(params)] as const,
  audit: (params?: QueryParams) => ['tc', 'audit', norm(params)] as const,
};
